if (!customElements.get("slideshow-section")) {
  customElements.define(
    "slideshow-section",
    class SlideshowSection extends HTMLElement {
      static get CONFIG() {
        return {
          BREAKPOINTS: {
            MOBILE: 768,
            TABLET: 1024,
            DESKTOP: 1280,
            LARGE_DESKTOP: 1536,
          },
          RESIZE_DEBOUNCE_DELAY: 100,
          KEYBOARD_KEYS: {
            LEFT_ARROW: 37,
            RIGHT_ARROW: 39,
          },
          SELECTORS: {
            SWIPER_CONTAINER: "[data-swiper]",
            SWIPER_WRAPPER: "[data-swiper-container]",
            SLIDE_SELECTOR: ".swiper-slide-selector",
            NAV_BUTTONS: ".wt-slider__nav-btn",
            PAGINATION: ".swiper-pagination",
            VIDEO: "video",
            FOCUSABLE_ELEMENTS: "a, button, input, textarea, select, [tabindex]",
          },
          CSS_CLASSES: {
            SWIPER: "swiper",
            SWIPER_CONTAINER: "wt-slider__container",
            SWIPER_WRAPPER: "swiper-wrapper",
            SWIPER_WRAPPER_CLASS: "wt-slider__wrapper",
            SWIPER_SLIDE: "swiper-slide",
            SWIPER_SLIDE_CLASS: "wt-slider__slide",
            SWIPER_INITIALIZED: "swiper-initialized",
            LOADING: "loading",
          },
        };
      }

      constructor() {
        super();

        this.state = {
          isInitializing: false,
          isInitialized: false,
          swiper: null,
          breakpoint: null,
        };

        this.configuration = null;

        this.boundMethods = {
          handleKeyboard: this.handleKeyboard.bind(this),
          handleResize: this.debounce(this.handleResize.bind(this), SlideshowSection.CONFIG.RESIZE_DEBOUNCE_DELAY),
          centerNavigation: this.centerNavigation.bind(this),
        };

        this.cleanupTasks = [];
      }

      connectedCallback() {
        if (this.state.isInitialized) {
          return;
        }

        try {
          this.initialize();
        } catch (error) {
          this.logError("Failed to initialize slideshow component", error);
        }
      }

      disconnectedCallback() {
        this.cleanup();
      }

      async initialize() {
        this.state.isInitialized = true;

        this.buildConfiguration();
        this.setupEventListeners();
        this.initializeSwiper();
      }

      setupEventListeners() {
        this.addEventListenerWithCleanup(window, "resize", this.boundMethods.handleResize);
        this.addEventListenerWithCleanup(window, "keydown", this.boundMethods.handleKeyboard);
      }

      addEventListenerWithCleanup(target, event, handler, options = {}) {
        target.addEventListener(event, handler, options);

        this.cleanupTasks.push(() => {
          target.removeEventListener(event, handler, options);
        });
      }

      handleResize() {
        try {
          this.centerNavigation();

          if (this.configuration.enableOnMedia) {
            this.handleMediaQueryChange();
          }
        } catch (error) {
          this.logError("Error handling resize", error);
        }
      }

      handleKeyboard(event) {
        const keyCode = event.keyCode || event.which;
        const focusedElement = document.activeElement;

        if (!this.state.swiper || !this.state.swiper.el.contains(focusedElement)) {
          return;
        }

        const keys = SlideshowSection.CONFIG.KEYBOARD_KEYS;
        if (keyCode === keys.LEFT_ARROW || keyCode === keys.RIGHT_ARROW) {
          event.preventDefault();
        }

        switch (keyCode) {
          case keys.LEFT_ARROW:
            this.state.swiper.slidePrev();
            break;
          case keys.RIGHT_ARROW:
            this.state.swiper.slideNext();
            break;
        }
      }

      initializeSwiper() {
        if (this.configuration.enableOnMedia) {
          this.state.breakpoint = window.matchMedia(this.configuration.enableOnMedia);
          this.handleMediaQueryChange();
        } else if (!this.state.swiper) {
          this.createSwiper();
        }
      }

      handleMediaQueryChange() {
        if (!this.state.breakpoint) {
          return;
        }

        if (this.state.breakpoint.matches && !this.state.swiper) {
          this.createSwiper();
        } else if (!this.state.breakpoint.matches && this.state.swiper) {
          this.destroySwiper();
        }
      }

      async createSwiper() {
        if (this.state.isInitializing || this.state.swiper) {
          return;
        }
        this.state.isInitializing = true;

        try {
          const container = this.querySelector(SlideshowSection.CONFIG.SELECTORS.SWIPER_CONTAINER);
          if (!container) {
            throw new Error("Swiper container not found");
          }

          this.setupSwiperDOM(container);
          this.state.swiper = new Swiper(container, this.configuration);
          container.classList.add(SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_INITIALIZED);
        } catch (error) {
          this.logError("Failed to initialize Swiper", error);
        } finally {
          this.state.isInitializing = false;
        }
      }

      setupSwiperDOM(container) {
        container.classList.add(
          SlideshowSection.CONFIG.CSS_CLASSES.SWIPER,
          SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_CONTAINER,
        );

        const wrapper = this.querySelector(SlideshowSection.CONFIG.SELECTORS.SWIPER_WRAPPER);
        if (wrapper) {
          wrapper.classList.add(
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_WRAPPER,
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_WRAPPER_CLASS,
          );
        }

        this.querySelectorAll(SlideshowSection.CONFIG.SELECTORS.SLIDE_SELECTOR).forEach((slide) => {
          slide.classList.add(
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_SLIDE,
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_SLIDE_CLASS,
          );
        });
      }

      destroySwiper() {
        if (!this.state.swiper) {
          return;
        }

        try {
          this.state.swiper.destroy(true, true);
          this.cleanupSwiperDOM();
        } catch (error) {
          this.logError("Error destroying Swiper", error);
        } finally {
          this.state.swiper = null;
        }
      }

      cleanupSwiperDOM() {
        const container = this.querySelector(SlideshowSection.CONFIG.SELECTORS.SWIPER_CONTAINER);
        const wrapper = this.querySelector(SlideshowSection.CONFIG.SELECTORS.SWIPER_WRAPPER);

        if (container) {
          container.classList.remove(
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER,
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_CONTAINER,
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_INITIALIZED,
          );
        }

        if (wrapper) {
          wrapper.classList.remove(
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_WRAPPER,
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_WRAPPER_CLASS,
          );
        }

        this.querySelectorAll(SlideshowSection.CONFIG.SELECTORS.SLIDE_SELECTOR).forEach((slide) => {
          slide.classList.remove(
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_SLIDE,
            SlideshowSection.CONFIG.CSS_CLASSES.SWIPER_SLIDE_CLASS,
          );
        });
      }

      handleTabindex(swiper) {
        const isSlidesGroup = this.hasAttribute("data-slides-group");
        const totalSlides = swiper.slides.length;
        const slidesPerView = swiper.params.slidesPerView || 1;

        if (!swiper || totalSlides <= slidesPerView) {
          this.setTabindexForAllSlides(swiper, "0");
          return;
        }

        if (isSlidesGroup) {
          this.handleTabindexForSlidesGroup(swiper);
        } else {
          this.handleTabindexForSingleSlide(swiper);
        }
      }

      handleTabindexForSlidesGroup(swiper) {
        const swiperRect = this.getBoundingClientRect();

        swiper.slides.forEach((slide) => {
          const slideRect = slide.getBoundingClientRect();
          const isFullyVisible = this.isSlideFullyVisible(slideRect, swiperRect);

          this.setTabindexForSlide(slide, isFullyVisible ? "0" : "-1");
        });
      }

      handleTabindexForSingleSlide(swiper) {
        swiper.slides.forEach((slide) => {
          this.setTabindexForSlide(slide, "-1");
        });

        const activeSlide = swiper.slides[swiper.activeIndex];
        if (!activeSlide) {
          return;
        }

        this.setTabindexForSlide(activeSlide, "0");
      }

      setTabindexForAllSlides(swiper, tabindex) {
        swiper.slides.forEach((slide) => {
          this.setTabindexForSlide(slide, tabindex);
        });
      }

      setTabindexForSlide(slide, tabindex) {
        const focusableElements = slide.querySelectorAll(SlideshowSection.CONFIG.SELECTORS.FOCUSABLE_ELEMENTS);

        focusableElements.forEach((element) => {
          if (element.hasAttribute("data-omit-tabindex")) {
            element.setAttribute("tabindex", "-1");
          } else {
            element.setAttribute("tabindex", tabindex);
          }
        });
      }

      isSlideFullyVisible(slideRect, swiperRect) {
        return (
          slideRect.left >= swiperRect.left &&
          slideRect.right <= swiperRect.right &&
          slideRect.top >= swiperRect.top &&
          slideRect.bottom <= swiperRect.bottom
        );
      }

      centerNavigation() {
        try {
          const buttons = this.querySelectorAll(SlideshowSection.CONFIG.SELECTORS.NAV_BUTTONS);
          const isMobile = window.innerWidth < SlideshowSection.CONFIG.BREAKPOINTS.MOBILE;

          buttons.forEach((button) => {
            button.style.display = isMobile ? "none" : "flex";
          });

          if (isMobile) {
            return;
          }

          const video = this.querySelector(SlideshowSection.CONFIG.SELECTORS.VIDEO);
          if (!video) {
            return;
          }

          const videoRect = video.getBoundingClientRect();
          const centerPosition = `${videoRect.height / 2}px`;

          buttons.forEach((button) => {
            button.style.top = centerPosition;
          });
        } catch (error) {
          this.logError("Error centering navigation", error);
        }
      }

      buildConfiguration() {
        const baseConfiguration = this.getBaseConfiguration();
        const customConfiguration = this.getCustomConfiguration();

        this.configuration = {
          ...baseConfiguration,
          ...customConfiguration,
          on: {
            ...baseConfiguration.on,
            ...customConfiguration.on,
          },
        };

        this.applyMobileModifications();
      }

      getBaseConfiguration() {
        return {
          loop: true,
          autoHeight: false,
          slidesPerView: 1,
          autoplay: false,
          threshold: 5,
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
          scrollbar: false,
          on: {
            afterInit: (swiper) => this.handleSwiperAfterInit(swiper),
            slideChangeTransitionEnd: (swiper) => this.handleTabindex(swiper),
          },
        };
      }

      getCustomConfiguration() {
        return {
          slidesPerView: 1.35,
          spaceBetween: 16,
          freeMode: false,
          speed: 150,
          grabCursor: false,
          centeredSlides: true,
          loop: true,
          slideToClickedSlide: true,
          enableOnMedia: "(min-width: 0px)",
          breakpoints: {
            [SlideshowSection.CONFIG.BREAKPOINTS.MOBILE]: {
              slidesPerView: 1.75,
            },
            [SlideshowSection.CONFIG.BREAKPOINTS.TABLET]: {
              slidesPerView: 2.5,
            },
            [SlideshowSection.CONFIG.BREAKPOINTS.DESKTOP]: {
              slidesPerView: 3.5,
              spaceBetween: 24,
            },
            [SlideshowSection.CONFIG.BREAKPOINTS.LARGE_DESKTOP]: {
              slidesPerView: 4.5,
              spaceBetween: 24,
            },
          },
        };
      }

      applyMobileModifications() {
        if (this.configuration.autoplay && window.innerWidth < SlideshowSection.CONFIG.BREAKPOINTS.MOBILE) {
          this.configuration.autoplay = false;
        }
      }

      handleSwiperAfterInit(swiper) {
        try {
          const dataSwiper = this.querySelector(SlideshowSection.CONFIG.SELECTORS.SWIPER_CONTAINER);
          if (dataSwiper) {
            dataSwiper.classList.remove(SlideshowSection.CONFIG.CSS_CLASSES.LOADING);
          }

          const dataSwiperContainer = this.querySelector(SlideshowSection.CONFIG.SELECTORS.SWIPER_WRAPPER);
          if (dataSwiperContainer) {
            dataSwiperContainer.classList.remove(SlideshowSection.CONFIG.CSS_CLASSES.LOADING);
          }

          this.centerNavigation();
          this.handleTabindex(swiper);
          this.dispatchSwiperReadyEvent(swiper);
        } catch (error) {
          this.logError("Error in afterInit handler", error);
        }
      }

      dispatchSwiperReadyEvent(swiper) {
        this.dispatchEvent(
          new CustomEvent("swiperReady", {
            detail: { swiper },
            bubbles: true,
          }),
        );
      }

      slideTo(slide) {
        if (!this.state.swiper || !slide) {
          this.logError("Cannot slide to target: swiper or slide not available");
          return;
        }

        try {
          this.state.swiper.autoplay?.stop();
          const index = Array.from(slide.parentNode.children).indexOf(slide);

          if (index >= 0) {
            this.state.swiper.slideTo(index);
          } else {
            this.logError("Invalid slide index calculated");
          }
        } catch (error) {
          this.logError("Error navigating to slide", error);
        }
      }

      debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
          const later = () => {
            clearTimeout(timeout);
            func(...args);
          };

          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
        };
      }

      logError(message, error = null) {
        const fullMessage = `SlideshowSection: ${message}`;
        if (error) {
          console.error(fullMessage, error);
        } else {
          console.warn(fullMessage);
        }
      }

      cleanup() {
        try {
          if (this.state.swiper) {
            this.destroySwiper();
          }

          this.cleanupTasks.forEach((cleanup) => {
            try {
              cleanup();
            } catch (error) {
              this.logError("Error during cleanup task", error);
            }
          });

          this.cleanupTasks = [];
          this.state.isInitialized = false;
          this.state.isInitializing = false;
          this.state.breakpoint = null;
        } catch (error) {
          this.logError("Error during component cleanup", error);
        }
      }
    },
  );
}
